// bluaIQ — THE PURCHASABLE-SUBTOTAL RULE, born BEFORE the first purchasable
// subtotal exists (dispatch 2026-07-31). Today NO money subtotal derives from
// takeoff anywhere (measured: unitPrice null on all 232 legacy items, the
// readonly display shows no money, Excel forbids numbers by seal, Budget is
// disconnected). The rule arrives before the data — cost S now, M-L after
// Budget wires up — so the worst scenario can never be built: a line correctly
// marked non-purchasable summing into a total the client reads as a purchase
// budget.
//
// A RULE IN THE MODEL TRAVELS WITH THE DATA; a filter in a view is forgotten
// in the next view. Every sum over takeoff lines passes through THIS module —
// takeoffSubtotalRule.smoke.ts reds any raw reduce that doesn't.
//
// TWO AXES, ORTHOGONAL (do not confuse under pressure):
//   line_role      → prevents DOUBLE COUNTING (summary/detail/standalone)
//   purchase_ready → decides PURCHASABILITY
// This module owns the second. Sheet routing owns the first.

/** The ONLY mark that admits a line into a purchasable subtotal: the literal
 *  true. Absent (every payload today — C3 will emit it), false, truthy junk
 *  ('true', 1) are all OUT. Fail-closed, the payment-whitelist reasoning. */
export function isPurchasableLine(line: { purchase_ready?: unknown }): boolean {
  return line.purchase_ready === true
}

export function purchasableLines<T extends { purchase_ready?: unknown }>(lines: readonly T[]): T[] {
  return lines.filter(isPurchasableLine)
}

/** A subtotal a client may read as purchase budget: ONLY purchase_ready lines. */
export function purchasableSubtotal<T extends { purchase_ready?: unknown }>(
  lines: readonly T[],
  pick: (line: T) => number,
): number {
  return purchasableLines(lines).reduce((sum, l) => sum + pick(l), 0)
}

/** The EXPLICIT, named escape for sums that are legitimately NOT purchasable
 *  totals (waste-factor display, honesty counters). The declared purpose is
 *  part of the call site — a reader sees WHY this sum may ignore
 *  purchase_ready, and the guard sees it passed through the model. A value
 *  from here must NEVER render as money or as a purchase quantity. */
export function nonPurchasableQuantitySum<T>(
  lines: readonly T[],
  pick: (line: T) => number,
  declaredPurpose: string,
): number {
  if (!declaredPurpose || declaredPurpose.trim().length === 0) {
    throw new Error('nonPurchasableQuantitySum requires a declared purpose — an undeclared escape is just a raw sum')
  }
  return lines.reduce((sum, l) => sum + pick(l), 0)
}
